import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { signedTimeZoneOffsetHours } from '#features/date/utils/signedTimeZoneOffsetHours';
import { updateTimeSettings } from '../model/profile';
import { useProfile } from './useProfile';

/**
 * Backfills the current user's `timeZoneOffset` from the browser when it's never
 * been set, so the daily chat digest can be sent at the user's local time instead
 * of the fixed default. Fires once per session, only when the offset is null — it
 * never overrides a value the user picked in Settings.
 *
 * Goes through the narrow `updateTimeSettings` mutation on purpose. It used to reuse the full
 * `updateProfile` — the very operation the profile form submits — and that cost us twice: the
 * backend marked `onboarding.profileSaved` on any call to it, so this silent backfill, firing a
 * second after registration, ticked the "Complete your profile" step for a newcomer who had
 * filled in nothing; and the mutation overwrites the whole profile row, so the one list this hook
 * forgot to re-send (platforms) was quietly cleared. The narrow mutation touches neither.
 *
 * Mount once in the authed layout.
 */
export const useEnsureTimeZoneOffset = () => {
  const { profile, refetchProfile } = useProfile();
  const { mutateAsync } = useMutation({ mutationFn: updateTimeSettings });
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!profile || profile.timeZoneOffset != null) return;

    firedRef.current = true;
    const timeZoneOffset = signedTimeZoneOffsetHours(new Date().getTimezoneOffset());

    void (async () => {
      try {
        const data = await mutateAsync({ timeZoneOffset });
        if (data?.__typename === 'CurrentUser') refetchProfile();
      } catch {
        // allow a retry on the next mount if the backfill failed
        firedRef.current = false;
      }
    })();
  }, [profile, mutateAsync, refetchProfile]);
};
